import {
    Box,
    Button,
    ButtonGroup,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Step,
    StepIcon,
    StepIndicator,
    StepNumber,
    Stepper,
    StepSeparator,
    StepStatus,
    StepTitle,
    Textarea,
    useDisclosure,
    useSteps,
} from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';

import { useCreateDashboardWidget, useUpdateDashboardWidget } from '../../api/dashboardWidget';
import { DashboardWidget } from '../../Dashboard/DashboardWidget/DashboardWidget';
import { ColumnOrder } from './ColumnOrder';
import { ColumnSelector } from './ColumnSelector';
import { FinalizeWidget } from './FinalizeWidget';
import { WidgetFilters } from './WidgetFilters';
import { WidgetType } from './WidgetType';

const steps = [
    { title: 'Import' },
    { title: 'Type' },
    { title: 'Filters' },
    { title: 'Columns' },
    { title: 'Column order' },
    { title: 'Finalize' },
    { title: 'Review' },
];

export const WidgetWizard = ({
    showImport,
    onClose,
    initialValue,
}: {
    showImport: boolean;
    onClose: () => void;
    initialValue?: Partial<DashboardWidgetDefinition>;
}) => {
    const { mutate: createWidget } = useCreateDashboardWidget();
    const { mutate: updateWidget } = useUpdateDashboardWidget();

    const [importString, setImportString] = useState('');
    const { activeStep, setActiveStep } = useSteps({
        index: showImport ? 0 : 1,
        count: steps.length,
    });

    const [widget, setWidget] = useState<Partial<DashboardWidgetDefinition>>({
        type: undefined,
        queryMeta: {},
        filters: [{ id: '', value: undefined }],
        columnIds: [],
        title: '',
        sort: undefined,
        ...initialValue,
    });

    const onImport = useCallback(() => {
        if (importString) {
            try {
                const data = JSON.parse(atob(importString));
                setWidget(data);
            } catch (ex) {
                // noop
            }
        }

        setImportString('');
        return setActiveStep(1);
    }, [importString, setActiveStep]);

    const onSave = useCallback(() => {
        initialValue?.id
            ? updateWidget(widget as DashboardWidgetDefinition, {
                  onSuccess: () => onClose(),
              })
            : createWidget(widget as DashboardWidgetDefinition, {
                  onSuccess: () => onClose(),
              });
    }, [createWidget, initialValue, onClose, updateWidget, widget]);

    const content = useMemo(() => {
        switch (activeStep) {
            case 0:
                return (
                    <Textarea
                        height="300px"
                        autoFocus
                        placeholder="Paste your widget import string here"
                        size="sm"
                        value={importString}
                        onChange={(ev) => setImportString(ev.target.value)}
                        resize="none"
                    />
                );
            case 1:
                return (
                    <WidgetType
                        selectedType={widget.type}
                        setSelectedType={(type) =>
                            setWidget((current) => ({
                                ...current,
                                type,
                                queryMeta: {
                                    ...current.queryMeta,
                                    dataVariant:
                                        type === 'securityMaster' || type === 'withdrawal'
                                            ? undefined
                                            : current.queryMeta?.dataVariant,
                                },
                            }))
                        }
                        selectedDataVariant={widget.queryMeta?.dataVariant as DataVariant}
                        setSelectedDataVariant={(dataVariant) =>
                            setWidget((current) => ({
                                ...current,
                                queryMeta: { ...current.queryMeta, dataVariant },
                            }))
                        }
                    />
                );
            case 2:
                return (
                    <WidgetFilters
                        widgetType={widget.type!}
                        filters={widget.filters!}
                        setFilters={(filters) =>
                            setWidget((current) => ({
                                ...current,
                                filters,
                            }))
                        }
                    />
                );
            case 3:
                return (
                    <ColumnSelector
                        widgetType={widget.type!}
                        columnIds={widget.columnIds!}
                        setColumnIds={(columnIds) =>
                            setWidget((current) => ({
                                ...current,
                                columnIds,
                            }))
                        }
                    />
                );
            case 4:
                return (
                    <ColumnOrder
                        widgetType={widget.type!}
                        columnIds={widget.columnIds!}
                        setColumnIds={(columnIds) =>
                            setWidget((current) => ({
                                ...current,
                                columnIds,
                            }))
                        }
                    />
                );
            case 5:
                return (
                    <FinalizeWidget
                        title={widget.title!}
                        setTitle={(title) =>
                            setWidget((current) => ({
                                ...current,
                                title,
                            }))
                        }
                        sort={widget.sort}
                        setSort={(sort) => setWidget((current) => ({ ...current, sort }))}
                        columnIds={widget.columnIds!}
                    />
                );
            case 6:
                return <DashboardWidget widgetDefinition={widget as DashboardWidgetDefinition} />;
        }
    }, [activeStep, importString, widget]);

    return (
        <Modal isOpen onClose={onClose} scrollBehavior="inside" size="6xl">
            <ModalOverlay />
            <ModalContent height="100%">
                <ModalHeader>{showImport ? 'Import' : 'Create'} Widget</ModalHeader>
                <ModalCloseButton />
                <ModalBody m={0} p={0}>
                    <Stepper
                        background="gray.800"
                        index={showImport ? activeStep : activeStep - 1}
                        colorScheme="teal"
                        p={6}
                        mb={4}
                    >
                        {steps.map((step, index) =>
                            !showImport && index === 0 ? null : (
                                <Step key={index}>
                                    <StepIndicator>
                                        <StepStatus
                                            complete={<StepIcon />}
                                            incomplete={<StepNumber />}
                                            active={<StepNumber />}
                                        />
                                    </StepIndicator>

                                    <Box flexShrink="0">
                                        <StepTitle>{step.title}</StepTitle>
                                    </Box>

                                    <StepSeparator />
                                </Step>
                            ),
                        )}
                    </Stepper>
                    <Box p={6}>{content}</Box>
                </ModalBody>

                <ModalFooter>
                    <ButtonGroup>
                        {activeStep > 0 && (
                            <Button onClick={() => setActiveStep(activeStep - 1)} isDisabled={activeStep === 1}>
                                Back
                            </Button>
                        )}
                        {activeStep === 0 ? (
                            <Button isDisabled={!importString.length} colorScheme="teal" onClick={onImport}>
                                Import
                            </Button>
                        ) : activeStep === steps.length - 1 ? (
                            <Button colorScheme="teal" onClick={onSave}>
                                Save
                            </Button>
                        ) : (
                            <Button
                                colorScheme="teal"
                                onClick={() => setActiveStep(activeStep + 1)}
                                isDisabled={!isOkToProceed(activeStep, widget)}
                            >
                                Next
                            </Button>
                        )}
                    </ButtonGroup>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const isOkToProceed = (step: number, widget: Partial<DashboardWidgetDefinition>) => {
    switch (step) {
        case 1:
            return (
                widget.type &&
                ((widget.type !== 'portfolio' && widget.type !== 'position') || widget.queryMeta?.dataVariant)
            );
        case 2:
            return widget.filters?.length && widget.filters[0].id.length && widget.filters[0].value !== undefined;
        case 3:
        case 4:
            return !!widget.columnIds?.length;
        case 5:
            return !!widget.title?.length;
        default:
            return true;
    }
};

export const useWidgetWizardControls = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [showImport, setShowImport] = useState(false);

    const createWidget = () => {
        setShowImport(false);
        onOpen();
    };
    const importWidget = () => {
        setShowImport(true);
        onOpen();
    };

    return {
        createWidget,
        importWidget,
        isOpen,
        onClose,
        showImport,
    };
};
